import storageHelpers from '@satellite/helpers/storage';
import yardSpotService from '@satellite/services/yardSpot-service';
import yardSpotAreaService from '@satellite/services/yardSpotArea-service';
import assetVisitService from '@satellite/services/asset-visit-service';
import messageThreadService from '@satellite/services/message-thread-service';
import customFormsService from '@satellite/services/custom-forms-service';
import { AssetVisitEventType, YardViewModeEnum, DepartTypeEnum } from '@nova/core';
import { concat } from 'lodash';
import i18n from '@/plugins/i18n.js';

function makeInitialYardData(state) {
  return {
    viewMode: state.viewMode,
    selectedWarehouseId: state.selectedWarehouse?.id ?? null
  };
}

function setYardData(userId, key, data, state) {
  const novaYardKey = storageHelpers.makeUserBoundedKey('nova_yard', userId);

  if (!localStorage.getItem(novaYardKey)) {
    localStorage.setItem(
      novaYardKey,
      JSON.stringify({
        ...makeInitialYardData(state)
      })
    );
  }

  let currentStorage = JSON.parse(localStorage.getItem(novaYardKey));
  currentStorage[key] = data;

  localStorage.setItem(novaYardKey, JSON.stringify({ ...currentStorage }));
}

const state = {
  selectedWarehouse: null,
  selectedSpot: null,
  selectedAssetVisit: null,
  isAssetVisitChatVisible: false,
  selectedSpots: [],
  selectedAreas: [],
  spots: [],
  spotAreas: [],
  assetVisits: [],
  isLoadingSpots: false,
  isLoadingAssetVisits: false,
  viewMode: YardViewModeEnum.GRID,
  assetContainerTriggers: []
};

const getters = {};

const actions = {
  checkSelectedWarehouse({ state }) {
    if (!state.selectedWarehouse?.id) {
      this.notify(i18n.global.t('yard.messages.pleaseSelectAWarehouseFirst'), 'error');
      return false;
    }

    return true;
  },
  async fakeCheckIn({ dispatch, state, commit }, data) {
    if (!dispatch('checkSelectedWarehouse')) {
      return;
    }
    commit('setIsLoadingAssetVisits', true);

    try {
      await assetVisitService.fakeCheckIn(state.selectedWarehouse.id, data);
      this.notify(i18n.global.t('yard.messages.checkInSuccessful'), 'success');
      dispatch('fetchAssetVisits');
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToCheckIn'), 'error');
      console.warn(error);
    } finally {
      commit('setIsLoadingAssetVisits', false);
    }
  },
  async fetchAssetVisits({ commit, dispatch }, params) {
    if (!dispatch('checkSelectedWarehouse')) {
      return;
    }
    commit('setIsLoadingAssetVisits', true);
    try {
      const assetVisits = await assetVisitService.getLatestAssetVisitsByWarehouseId(
        state.selectedWarehouse.id,
        params
      );

      const assetContainers = await assetVisitService.getAssetContainersByWarehouseId(
        state.selectedWarehouse.id,
        params
      );

      // We join both asset visit and asset containers to show up inside the list
      // As they are treated as the same entity for the waiting list
      // We need to filter out the asset containers that are already assigned to an asset visit
      // And the ones that are departed
      const assets = concat(
        assetVisits,
        assetContainers
          .filter(ac => !ac.spotAssignments.length)
          .filter(
            ac => !ac.assetContainerEvents.some(e => e.eventType === AssetVisitEventType.Departed)
          )
      );

      commit('setAssetVisits', assets || []);

      return assets;
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToGetAssetVisits'), 'error');
      console.warn(error);
    } finally {
      commit('setIsLoadingAssetVisits', false);
    }
  },
  async fetchSpots({ commit, state, dispatch }, params) {
    if (!dispatch('checkSelectedWarehouse')) {
      return;
    }
    commit('setIsLoadingSpots', true);
    try {
      const spots = await yardSpotService.getManyByWarehouseId(state.selectedWarehouse.id, params);
      commit('setSpots', spots || []);
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToGetSpots', 2), 'error');
      console.warn(error);
    } finally {
      commit('setIsLoadingSpots', false);
    }
  },
  async fetchSpot({ commit }, id) {
    commit('setIsLoadingSpots', true);
    try {
      const spot = await yardSpotService.getOneById(id, {});

      return spot;
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToGetSpots'), 'error');
      console.warn(error);
    } finally {
      commit('setIsLoadingSpots', false);
    }
  },
  async fetchSpotAreas({ commit, state, dispatch }, params) {
    if (!dispatch('checkSelectedWarehouse')) {
      return;
    }
    try {
      const spotAreas = await yardSpotAreaService.getManyByWarehouseId(
        state.selectedWarehouse.id,
        params
      );
      commit('setSpotAreas', spotAreas || []);
      commit('setSelectedAreas', spotAreas.map(area => area.id) || []);
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToGetSpotAreas'), 'error');
      console.warn(error);
    }
  },
  async createSpotAssignment({ dispatch, commit }, data) {
    if (!dispatch('checkSelectedWarehouse')) {
      return;
    }

    try {
      const spotAssignment = await yardSpotService.createSpotAssignment(data);
      this.notify(i18n.global.t('yard.messages.spotAssignmentCreatedSuccessfully'), 'success');

      dispatch('fetchSpot', data.spotId).then(spot => commit('setSelectedSpot', spot));

      return spotAssignment;
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToCreateSpotAssignment'), 'error');
      console.warn(error);
    }
  },
  async deleteSpotAssignment({ dispatch, commit }, id) {
    if (!dispatch('checkSelectedWarehouse')) {
      return;
    }
    commit('setIsLoadingSpots', true);
    try {
      const spotAssignment = await yardSpotService.deleteSpotAssignment(id);

      dispatch('fetchSpot', spotAssignment.spotId).then(spot => commit('setSelectedSpot', spot));
      dispatch('fetchAssetVisits');

      this.notify(i18n.global.t('yard.messages.spotAssignmentDeletedSuccessfully'), 'success');
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToDeleteSpotAssignment'), 'error');
      console.warn(error);
    } finally {
      commit('setIsLoadingSpots', false);
    }
  },
  async departSpotAssignment({ commit }, { id, assetType }) {
    commit('setIsLoadingAssetVisits', true);
    try {
      await yardSpotService.departSpotAssignment(id, assetType);
      this.notify(i18n.global.t('yard.messages.assetVisitDepartedSuccessfully'), 'success');
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToDepartAssetVisit'), 'error');
      console.warn(error);
    } finally {
      commit('setIsLoadingAssetVisits', false);
    }
  },
  async updateSpotAssignment({ commit, dispatch }, { id, data }) {
    commit('setIsLoadingSpots', true);
    try {
      const spotId = data.spotId;
      const spotAssignment = await yardSpotService.updateSpotAssignment(id, data);

      dispatch('fetchSpot', spotId).then(spot => commit('setSelectedSpot', spot));

      this.notify(i18n.global.t('yard.messages.spotAssignmentUpdatedSuccessfully'), 'success');

      return spotAssignment;
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToUpdateSpotAssignment'), 'error');
      console.warn(error);
    } finally {
      commit('setIsLoadingSpots', false);
    }
  },
  async reassignSpotAssignment({ commit, dispatch, state }, { id, data }) {
    commit('setIsLoadingSpots', true);
    try {
      const spotAssignment = await yardSpotService.reassignSpotAssignment(id, {
        reason: data.reason,
        spotId: data.spotId,
        moveContainer: data.moveContainer
      });

      dispatch('fetchSpot', data.oldSpotId ?? spotAssignment.spotId).then(spot =>
        commit(
          'setSpots',
          state.spots.map(s => (s.id === spot.id ? spot : s))
        )
      );
      dispatch('fetchSpot', data.spotId).then(spot => commit('setSelectedSpot', spot));

      this.notify(i18n.global.t('yard.messages.spotAssignmentReassignedSuccessfully'), 'success');

      return spotAssignment;
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToReassignSpotAssignment'), 'error');
      console.warn(error);
    } finally {
      commit('setIsLoadingSpots', false);
    }
  },
  async createSpotArea({ dispatch, state }, data) {
    if (!dispatch('checkSelectedWarehouse')) {
      return;
    }

    try {
      const spotArea = await yardSpotAreaService.create({
        ...data,
        warehouseId: state.selectedWarehouse.id
      });
      dispatch('fetchSpotAreas');
      this.notify(i18n.global.t('yard.messages.spotAreaCreatedSuccessfully'), 'success');
      return spotArea;
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToCreateSpotArea'), 'error');
      console.warn(error);
    }
  },
  async updateSpotArea({ dispatch }, { id, data }) {
    if (!dispatch('checkSelectedWarehouse')) {
      return;
    }
    try {
      const spotArea = await yardSpotAreaService.update(id, data);
      dispatch('fetchSpotAreas');
      this.notify(i18n.global.t('yard.messages.spotAreaUpdatedSuccessfully'), 'success');
      return spotArea;
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToUpdateSpotArea'), 'error');
      console.warn(error);
    }
  },
  async deleteSpotArea({ dispatch }, id) {
    if (!dispatch('checkSelectedWarehouse')) {
      return;
    }
    try {
      await yardSpotAreaService.delete(id);
      this.notify(i18n.global.t('yard.messages.spotAreaDeletedSuccessfully'), 'success');
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToDeleteSpotArea'), 'error');
      console.warn(error);
    }
  },
  async updateSpot({ commit }, { id, data }) {
    try {
      const spot = await yardSpotService.update(id, data);

      commit('setSelectedSpot', spot);
      commit(
        'setSpots',
        state.spots.map(s => (s.id === spot.id ? spot : s))
      );

      this.notify(i18n.global.t('yard.messages.spotUpdatedSuccessfully'), 'success');
      return spot;
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToUpdateSpots'), 'error');
      console.warn(error);
    }
  },
  async createSpot({ dispatch, commit }, data) {
    if (!dispatch('checkSelectedWarehouse')) {
      return;
    }
    commit('setIsLoadingSpots', true);
    try {
      const spot = await yardSpotService.create({
        ...data
      });
      dispatch('fetchSpots');
      this.notify(i18n.global.t('yard.messages.spotsCreatedSuccessfully'), 'success');
      return spot;
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToCreateSpots'), 'error');
      console.warn(error);
    } finally {
      commit('setIsLoadingSpots', false);
    }
  },
  async createManySpot({ dispatch, commit }, data) {
    if (!dispatch('checkSelectedWarehouse')) {
      return;
    }
    commit('setIsLoadingSpots', true);
    try {
      await yardSpotService.createMany(data);
      dispatch('fetchSpots');
      this.notify(i18n.global.t('yard.messages.spotsCreatedSuccessfully', 2), 'success');
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToCreateSpots', 2), 'error');
      console.warn(error);
    } finally {
      commit('setIsLoadingSpots', false);
    }
  },
  async closeSpot({ state, commit }, { id, data }) {
    commit('setIsLoadingSpots', true);
    try {
      const spot = await yardSpotService.close(id, data);

      commit('setSelectedSpot', spot);
      commit(
        'setSpots',
        state.spots.map(s => (s.id === spot.id ? spot : s))
      );
      this.notify(i18n.global.t('yard.messages.spotClosedSuccessfully'), 'success');
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToCloseSpot'), 'error');
      console.warn(error);
    } finally {
      commit('setIsLoadingSpots', false);
    }
  },
  async openSpot({ state, commit }, { id, data }) {
    commit('setIsLoadingSpots', true);
    try {
      const spot = await yardSpotService.open(id, data);

      commit('setSelectedSpot', spot);
      commit(
        'setSpots',
        state.spots.map(s => (s.id === spot.id ? spot : s))
      );

      this.notify(i18n.global.t('yard.messages.spotOpenedSuccessfully'), 'success');
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToOpenSpot'), 'error');
      console.warn(error);
    } finally {
      commit('setIsLoadingSpots', false);
    }
  },
  async deleteSpot({ commit }, id) {
    commit('setIsLoadingSpots', true);
    try {
      await yardSpotService.delete(id);

      commit('setSelectedSpot', null);
      commit(
        'setSpots',
        state.spots.filter(s => s.id !== id)
      );

      this.notify(i18n.global.t('yard.messages.spotsDeletedSuccessfully'), 'success');
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToDeleteSpots'), 'error');
      console.warn(error);
    } finally {
      commit('setIsLoadingSpots', false);
    }
  },
  async deleteManySpot({ dispatch, commit }, ids) {
    commit('setIsLoadingSpots', true);
    try {
      await yardSpotService.deleteMany(ids);
      dispatch('fetchSpots');

      this.notify(i18n.global.t('yard.messages.spotsDeletedSuccessfully', 2), 'success');
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToDeleteSpots', 2), 'error');
      console.warn(error);
    } finally {
      commit('setIsLoadingSpots', false);
    }
  },
  async fetchAssetContainerTriggers({ commit, state, dispatch }) {
    if (!dispatch('checkSelectedWarehouse')) {
      return;
    }

    try {
      const triggers = await customFormsService.getAssetContainerTriggers(
        state.selectedWarehouse.id,
        { isActive: true }
      );
      commit('setAssetContainerTriggers', triggers || []);
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToGetAssetContainerTriggers'), 'error');
      console.warn(error);
    }
  },
  async messageDriver({}, { assetVisitId, content }) {
    try {
      const messageThread = await messageThreadService.getMessageThreadByAssetVisitId(assetVisitId);
      if (messageThread?.id) {
        await messageThreadService.sendMessage(messageThread.id, { content });
        this.notify(i18n.global.t('yard.messages.messageSentSuccessfully'), 'success');
      } else {
        this.notify(i18n.global.t('yard.messages.failedToSendMessage'), 'error');
      }
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToSendMessage'), 'error');
      console.warn(error);
    }
  },
  /*
   * @param {String} assetVisitId
   * @param {String} assetVisitAction - 'keep' | 'reassign' | 'depart'
   * @param {Object} assetContainerActions - { assetContainerId: action - 'keep' | 'drop' }
   */
  async detachAssetVisit(
    { commit, dispatch },
    {
      assetVisitId,
      assetVisitAction,
      assetContainerActions,
      reassignData: { newSpotId, spotAssignmentId }
    }
  ) {
    commit('setIsLoadingAssetVisits', true);
    try {
      // First, deal with the asset containers, do the 'waiting' actions first
      for (const [assetContainerId, action] of Object.entries(assetContainerActions)) {
        if (action === 'waiting') {
          // This will set the assetContainer.assetVisitId to null
          // And update the spotAssignment.assetContainer to the new assetContainer
          await assetVisitService.detachContainer(assetVisitId, assetContainerId);
        }
      }

      // Then the drop actions, so the last one will remain in the spot
      // According to the API specs for now.
      for (const [assetContainerId, action] of Object.entries(assetContainerActions)) {
        if (action === 'drop') {
          await assetVisitService.detachContainer(assetVisitId, assetContainerId);
        }
      }

      // After all containers are set, deal with the asset visit
      if (assetVisitAction === 'depart') {
        await yardSpotService.departSpotAssignment(spotAssignmentId, DepartTypeEnum.AssetVisit);
      }

      if (assetVisitAction === 'reassign') {
        await yardSpotService.reassignSpotAssignment(spotAssignmentId, {
          spotId: newSpotId,
          reason: i18n.global.t('yard.messages.detachAsset')
        });
      }

      this.notify(i18n.global.t('yard.messages.assetVisitDetachedSuccessfully'), 'success');

      dispatch('fetchAssetVisits');

      return true;
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToDetachAssetVisit'), 'error');
      console.warn(error);
    } finally {
      commit('setIsLoadingAssetVisits', false);
    }
  },
  async attachAssetVisit({ commit, dispatch }, { assetVisitId, assetContainerActions }) {
    commit('setIsLoadingAssetVisits', true);
    try {
      for (const [assetContainerId, action] of Object.entries(assetContainerActions)) {
        if (action === 'attach') {
          await assetVisitService.attachContainer(assetVisitId, assetContainerId);
        }
      }

      dispatch('fetchAssetVisits');

      this.notify(i18n.global.t('yard.messages.assetVisitAttachedSuccessfully'), 'success');
      return true;
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToAttachAssetVisit'), 'error');
      console.warn(error);
    } finally {
      commit('setIsLoadingAssetVisits', false);
    }
  },
  async updateAssetContainerStatus({ dispatch, commit, state }, { assetContainerId, eventType }) {
    commit('setIsLoadingSpots', true);
    try {
      await assetVisitService.updateAssetContainerStatus(assetContainerId, eventType);

      if (state.selectedSpot?.id) {
        dispatch('fetchSpot', state.selectedSpot.id).then(spot => commit('setSelectedSpot', spot));
      }

      if (state.selectedAssetVisit?.id) {
        dispatch('fetchAssetVisits').then(assetVisits =>
          commit(
            'setSelectedAssetVisit',
            assetVisits.find(av => av.id === state.selectedAssetVisit.id)
          )
        );
      }

      this.notify(
        i18n.global.t('yard.messages.assetContainerStatusUpdatedSuccessfully'),
        'success'
      );
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToUpdateAssetContainerStatus'), 'error');
      console.warn(error);
    } finally {
      commit('setIsLoadingSpots', false);
    }
  },
  async updateAssetContainer({ commit, dispatch }, { assetContainerId, data }) {
    commit('setIsLoadingSpots', true);
    try {
      const response = await assetVisitService.updateAssetContainer(assetContainerId, data);

      if (state.selectedSpot?.id) {
        dispatch('fetchSpot', state.selectedSpot.id).then(spot => commit('setSelectedSpot', spot));
      }

      if (state.selectedAssetVisit?.id) {
        dispatch('fetchAssetVisits').then(assetVisits =>
          commit(
            'setSelectedAssetVisit',
            assetVisits.find(av => av.id === state.selectedAssetVisit.id)
          )
        );
      }

      this.notify(i18n.global.t('yard.messages.assetContainerUpdatedSuccessfully'), 'success');
      return response;
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToUpdateAssetContainer'), 'error');
      console.warn(error);
    } finally {
      commit('setIsLoadingSpots', false);
    }
  },
  async updateAssetVisitStatus({ dispatch, commit }, { assetVisitId, eventType }) {
    commit('setIsLoadingAssetVisits', true);
    try {
      await assetVisitService.updateAssetVisitStatus(assetVisitId, eventType);

      dispatch('fetchSpots');
      dispatch('fetchAssetVisits');

      // The asset visit will not be available anymore
      if (eventType === AssetVisitEventType.Departed) {
        commit('setSelectedAssetVisit', null);
      }

      this.notify(i18n.global.t('yard.messages.assetVisitStatusUpdatedSuccessfully'), 'success');
    } catch (error) {
      this.notify(i18n.global.t('yard.messages.failedToUpdateAssetVisitStatus'), 'error');
      console.warn(error);
    } finally {
      commit('setIsLoadingAssetVisits', false);
    }
  },
  setViewMode({ commit, state, rootState }, { value }) {
    commit('setViewMode', value);

    setYardData(rootState.Auth.me.id, 'viewMode', value, state);
  },
  persistSelectedWarehouse({ state, rootState }, warehouseId) {
    const userId = rootState.Auth.me.id;
    setYardData(userId, 'selectedWarehouseId', warehouseId, state);
  },
  persistListViewColumns({ state, rootState }, columns) {
    const userId = rootState.Auth.me.id;
    setYardData(userId, 'listViewColumns', columns, state);
  },
  fetchPreviousYardData({ rootState }) {
    const userId = rootState.Auth.me.id;
    const novaYardKey = storageHelpers.makeUserBoundedKey('nova_yard', userId);

    const data = localStorage.getItem(novaYardKey);

    if (!data) {
      return null;
    }

    return JSON.parse(data);
  }
};

const mutations = {
  setSelectedWarehouse(state, warehouse) {
    state.selectedWarehouse = warehouse;
    if (!warehouse?.id) {
      state.selectedAreas = [];
      state.spots = [];
      state.spotAreas = [];
      state.assetVisits = [];
      state.assetContainerTriggers = [];
    }
    state.selectedSpot = null;
  },
  setSelectedSpot(state, spot) {
    state.selectedAssetVisit = null;
    state.isAssetVisitChatVisible = false;
    state.selectedSpot = spot;

    // update the spot in the spots array if it is not null
    if (spot) {
      state.spots = state.spots.map(s => (s.id === spot.id ? spot : s));
    }

    // Clean up the drag-active class after a while
    setTimeout(() => {
      const dropZones = document.querySelectorAll('.drop-zone');
      dropZones?.forEach(zone => {
        zone.classList.remove('drag-active');
      });
    }, 100);
  },
  setSelectedAssetVisit(state, assetVisit) {
    state.selectedSpot = null;
    state.selectedAssetVisit = assetVisit;
    state.isAssetVisitChatVisible = false;
  },
  setSelectedSpots(state, spots) {
    state.selectedSpots = spots;
  },
  setIsAssetVisitChatVisible(state, value) {
    state.isAssetVisitChatVisible = value;
  },
  setSelectedAreas(state, areas) {
    state.selectedAreas = areas;
    state.selectedSpot = null;
  },
  setSpots(state, spots) {
    state.spots = spots;
  },
  setSpotAreas(state, spotAreas) {
    state.spotAreas = spotAreas;
  },
  setAssetVisits(state, assetVisits) {
    state.assetVisits = assetVisits;
  },
  setIsLoadingSpots(state, value) {
    state.isLoadingSpots = value;
  },
  setIsLoadingAssetVisits(state, value) {
    state.isLoadingAssetVisits = value;
  },
  setViewMode(state, value) {
    state.viewMode = value;
  },
  setAssetContainerTriggers(state, value) {
    state.assetContainerTriggers = value;
  }
};

export default {
  namespaced: true,
  name: 'Yard',
  state,
  getters,
  actions,
  mutations
};
